import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/privacy')({
  component: PrivacyPage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function PrivacyPage() {
  return (
    <div>
      <Helmet>
        <title>Privacy Policy | One-Email</title>
        <meta name="description" content="One-Email Privacy Policy. How we collect, use, and protect your data. Minimal data collection, no third-party tracking, EU-hosted infrastructure." />
        <link rel="canonical" href="https://one-email.com/legal/privacy" />
      </Helmet>

      <h1 style={s.heading}>Privacy Policy</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> We collect minimal data to provide the Service. Email content passes through our servers for delivery but is never stored. We never sell your data. All infrastructure is hosted in the EU. We use no third-party tracking or advertising.
      </div>

      {/* 1. Who We Are */}
      <h2 id="who-we-are" style={s.h2}>1. Who We Are</h2>
      <p style={s.p}>
        One-Email is operated by IMP Capital AB, Rotebrunnsv&auml;gen 3, 393 55 Kalmar, Sweden (org.nr: 559452-9074). We provide an email infrastructure API and SMTP relay for developers and businesses.
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Privacy contact:</strong> privacy@one-email.com</li>
        <li style={s.li}><strong>Supervisory authority:</strong> Integritetsskyddsmyndigheten (IMY), Box 8114, 104 20 Stockholm, Sweden &mdash; <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer">imy.se</a></li>
      </ul>

      <h3 style={s.h3}>Our Roles</h3>
      <ul style={s.ul}>
        <li style={s.li}><strong>Data controller</strong> for your account data (your name, email, login information). We decide how this data is used.</li>
        <li style={s.li}><strong>Data processor</strong> for email recipient data (the email addresses and metadata of people you email through our Service). You decide who to email and what to send &mdash; we process that data on your behalf, per your API instructions.</li>
      </ul>
      <p style={s.p}>
        This distinction matters. You are responsible for having lawful basis to email your recipients. Our obligations are about how we handle the data you give us.
      </p>

      {/* 2. Data We Collect */}
      <h2 id="data-collected" style={s.h2}>2. Data We Collect</h2>

      <h3 style={s.h3}>Account Data (We Are the Data Controller)</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Data Point</th>
              <th style={thStyle}>Purpose</th>
              <th style={thStyle}>Legal Basis (GDPR)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Email address</td>
              <td style={tdStyle}>Authentication, billing notifications, service communications</td>
              <td style={tdStyle}>Contract performance (Art. 6(1)(b))</td>
            </tr>
            <tr>
              <td style={tdStyle}>Name</td>
              <td style={tdStyle}>Dashboard display, communications</td>
              <td style={tdStyle}>Contract performance (Art. 6(1)(b))</td>
            </tr>
            <tr>
              <td style={tdStyle}>Password hash (bcrypt, if email+password auth)</td>
              <td style={tdStyle}>Authentication</td>
              <td style={tdStyle}>Contract performance (Art. 6(1)(b))</td>
            </tr>
            <tr>
              <td style={tdStyle}>GitHub profile info (if GitHub OAuth)</td>
              <td style={tdStyle}>Authentication, avatar display</td>
              <td style={tdStyle}>Contract performance (Art. 6(1)(b))</td>
            </tr>
            <tr>
              <td style={tdStyle}>IP address at login</td>
              <td style={tdStyle}>Security, abuse prevention</td>
              <td style={tdStyle}>Legitimate interest (Art. 6(1)(f))</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>Email Metadata (We Are the Data Processor)</h3>
      <p style={s.p}>When you send email through One-Email, we store metadata about each message:</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Data Point</th>
              <th style={thStyle}>Format</th>
              <th style={thStyle}>Retention</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Sender address (from)</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Recipient addresses (to, cc, bcc)</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Subject line</td><td style={tdStyle}><strong>SHA-256 hash only</strong> (not human-readable)</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Timestamps (created, sent, delivered, opened, clicked)</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Delivery status (queued/sent/delivered/bounced/complained)</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Transport used (transactional/marketing/dedicated/standby)</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Sending IP address</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>Tags (your key-value pairs)</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
            <tr><td style={tdStyle}>SMTP message ID</td><td style={tdStyle}>Plaintext</td><td style={tdStyle}>Per your tier</td></tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>Data We Do NOT Store</h3>
      <p style={s.p}>This is our strongest privacy position. We believe the best way to protect data is to not have it.</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Data Point</th>
              <th style={thStyle}>What Happens</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}><strong>Email body (HTML and plaintext)</strong></td>
              <td style={tdStyle}>Processed in memory during sending. Discarded immediately after delivery. We never store email content.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Custom headers</strong></td>
              <td style={tdStyle}>Discarded after sending.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Attachment content</strong></td>
              <td style={tdStyle}>Stored temporarily in Cloudflare R2 during the send process (seconds to minutes). Deleted immediately after delivery confirmation. Orphaned attachments purged by daily cleanup after 24 hours.</td>
            </tr>
            <tr>
              <td style={tdStyle}><strong>Credit card / payment details</strong></td>
              <td style={tdStyle}>Handled entirely by Polar. We never see, receive, or store payment information. We store only your tier, billing status, and Polar subscription ID.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 style={s.h3}>Billing Data</h3>
      <p style={s.p}>
        All payment processing is handled by <a href="https://polar.sh/privacy" target="_blank" rel="noopener noreferrer">Polar</a>. We store only:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Your subscription tier</li>
        <li style={s.li}>Billing status (active, past due, cancelled)</li>
        <li style={s.li}>Polar subscription ID</li>
      </ul>
      <p style={s.p}>We do not store credit card numbers, bank account details, or payment tokens.</p>

      <h3 style={s.h3}>Usage Data</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Data Point</th>
              <th style={thStyle}>Purpose</th>
              <th style={thStyle}>Legal Basis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>API call logs (endpoint, timestamp, response code)</td>
              <td style={tdStyle}>Service operation, debugging, rate limiting</td>
              <td style={tdStyle}>Legitimate interest (Art. 6(1)(f))</td>
            </tr>
            <tr>
              <td style={tdStyle}>IP addresses from API requests</td>
              <td style={tdStyle}>Security, rate limiting, abuse prevention</td>
              <td style={tdStyle}>Legitimate interest (Art. 6(1)(f))</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        We do <strong>not</strong> collect dashboard usage analytics. We have no Google Analytics, no Mixpanel, no Segment, no Plausible, and no third-party tracking of any kind.
      </p>

      {/* 3. How We Use Your Data */}
      <h2 id="how-used" style={s.h2}>3. How We Use Your Data</h2>
      <ul style={s.ul}>
        <li style={s.li}>To deliver emails on your behalf</li>
        <li style={s.li}>To provide delivery analytics and status tracking</li>
        <li style={s.li}>To process billing via Polar</li>
        <li style={s.li}>To send you service-related notifications (billing, security, AUP enforcement)</li>
        <li style={s.li}>To prevent abuse and protect our infrastructure (monitoring bounce/complaint rates, enforcing ramp limits)</li>
        <li style={s.li}>To comply with legal obligations (e.g., responding to lawful data requests)</li>
      </ul>
      <p style={s.p}>
        We do <strong>not</strong> use your data for advertising. We do <strong>not</strong> sell, rent, or trade your personal data. We do <strong>not</strong> use email content or metadata to train AI/ML models.
      </p>

      {/* 4. Legal Basis for Processing */}
      <h2 id="legal-basis" style={s.h2}>4. Legal Basis for Processing (GDPR Article 6)</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Processing Activity</th>
              <th style={thStyle}>Legal Basis</th>
              <th style={thStyle}>Article</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Delivering emails you instruct us to send</td><td style={tdStyle}>Contract performance</td><td style={tdStyle}>6(1)(b)</td></tr>
            <tr><td style={tdStyle}>Storing email metadata for analytics/status</td><td style={tdStyle}>Contract performance</td><td style={tdStyle}>6(1)(b)</td></tr>
            <tr><td style={tdStyle}>Account creation and management</td><td style={tdStyle}>Contract performance</td><td style={tdStyle}>6(1)(b)</td></tr>
            <tr><td style={tdStyle}>Abuse prevention and platform security</td><td style={tdStyle}>Legitimate interest</td><td style={tdStyle}>6(1)(f)</td></tr>
            <tr><td style={tdStyle}>Service notifications to account owners</td><td style={tdStyle}>Legitimate interest</td><td style={tdStyle}>6(1)(f)</td></tr>
            <tr><td style={tdStyle}>Complying with legal obligations</td><td style={tdStyle}>Legal obligation</td><td style={tdStyle}>6(1)(c)</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        We do <strong>not</strong> rely on consent as a legal basis for our own processing. Consent is your responsibility for your relationship with your email recipients.
      </p>

      {/* 5. Data Storage and Location */}
      <h2 id="storage" style={s.h2}>5. Data Storage and Location</h2>
      <p style={s.p}>All persistent data is stored in the European Union.</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Component</th>
              <th style={thStyle}>Provider</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Data</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Database (Postgres)</td>
              <td style={tdStyle}>Neon</td>
              <td style={tdStyle}>EU region</td>
              <td style={tdStyle}>All persistent data: accounts, email metadata, API key hashes, DKIM keys (encrypted), domains, suppression lists</td>
            </tr>
            <tr>
              <td style={tdStyle}>SMTP infrastructure</td>
              <td style={tdStyle}>Scaleway</td>
              <td style={tdStyle}>Paris, France (EU)</td>
              <td style={tdStyle}>Email content in transit for delivery, SMTP delivery logs, queue files (encrypted at rest)</td>
            </tr>
            <tr>
              <td style={tdStyle}>API gateway, queues, caching</td>
              <td style={tdStyle}>Cloudflare</td>
              <td style={tdStyle}>Global edge (routing)</td>
              <td style={tdStyle}>API requests, email payloads in transit via Queue, cached auth/suppression data (KV)</td>
            </tr>
            <tr>
              <td style={tdStyle}>Template/attachment storage</td>
              <td style={tdStyle}>Cloudflare R2</td>
              <td style={tdStyle}>EU</td>
              <td style={tdStyle}>Email templates, temporary attachment storage</td>
            </tr>
            <tr>
              <td style={tdStyle}>Billing</td>
              <td style={tdStyle}>Polar</td>
              <td style={tdStyle}>Per <a href="https://polar.sh/privacy" target="_blank" rel="noopener noreferrer">Polar&apos;s privacy policy</a></td>
              <td style={tdStyle}>Name, email, subscription details</td>
            </tr>
            <tr>
              <td style={tdStyle}>DNS management (auto-configure only)</td>
              <td style={tdStyle}>Vercel</td>
              <td style={tdStyle}>US (Vercel API)</td>
              <td style={tdStyle}>Encrypted OAuth token, domain DNS records &mdash; only when you explicitly authorize via OAuth</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        <strong>Note on Cloudflare&apos;s global edge:</strong> While our API gateway runs on Cloudflare Workers at the nearest edge location (meaning an API request may be initially processed in any country), email content and persistent data are stored only in the EU. Email content is not persisted at the edge. Cloudflare&apos;s DPA includes EU Standard Contractual Clauses and Cloudflare is certified under the EU-US Data Privacy Framework.
      </p>

      {/* 6. Data Sharing */}
      <h2 id="sharing" style={s.h2}>6. Data Sharing</h2>
      <p style={s.p}>
        We share data only with the infrastructure providers listed above, which are necessary to deliver the Service. Every sub-processor is bound by a data processing agreement with data protection obligations no less protective than ours.
      </p>
      <p style={s.p}>
        We do <strong>not</strong> sell your data. We do <strong>not</strong> share data with advertisers. We do <strong>not</strong> use third-party analytics or tracking services.
      </p>
      <p style={s.p}>
        Our complete and current list of sub-processors is published at <a href="/legal/subprocessors">/legal/subprocessors</a>. We will notify you at least 30 days before adding a new sub-processor.
      </p>

      <h3 style={s.h3}>Explicitly NOT Used</h3>
      <ul style={s.ul}>
        <li style={s.li}><strong>Amazon Web Services (SES or otherwise)</strong> &mdash; We do not use AWS SES or any AWS service. We do not fall back to any third-party email relay.</li>
        <li style={s.li}><strong>Google Analytics, Mixpanel, Segment, or any analytics provider</strong> &mdash; Zero third-party tracking.</li>
        <li style={s.li}><strong>Advertising networks or SDKs</strong> &mdash; No ad tech of any kind.</li>
      </ul>

      {/* 7. Data Retention */}
      <h2 id="retention" style={s.h2}>7. Data Retention</h2>

      <h3 style={s.h3}>Email Metadata Retention (by tier)</h3>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Tier</th>
              <th style={thStyle}>Retention Period</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}>Free</td><td style={tdStyle}>3 days</td></tr>
            <tr><td style={tdStyle}>Starter</td><td style={tdStyle}>7 days</td></tr>
            <tr><td style={tdStyle}>Pro</td><td style={tdStyle}>30 days</td></tr>
            <tr><td style={tdStyle}>Growth</td><td style={tdStyle}>90 days</td></tr>
            <tr><td style={tdStyle}>Scale</td><td style={tdStyle}>1 year</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>Email metadata is automatically deleted by a daily cleanup job after the retention period expires.</p>

      <h3 style={s.h3}>Suppression Lists</h3>
      <p style={s.p}>
        Hard bounce, complaint, and unsubscribe records are retained for the lifetime of your account (independent of tier-based retention). This is necessary to prevent sending to addresses that have bounced, complained, or unsubscribed. You can manage your suppression list via the API or dashboard.
      </p>

      <h3 style={s.h3}>Account Data</h3>
      <ul style={s.ul}>
        <li style={s.li}>Retained while your account is active.</li>
        <li style={s.li}>On cancellation: retained for <strong>30 days</strong> (grace period for reactivation or data export).</li>
        <li style={s.li}>After 30 days: permanently deleted via cascade &mdash; email events, metadata, enforcement logs, suppression lists, DKIM keys, domains, webhooks, API keys, R2 objects, account record, KV cache entries. The account record is anonymized (email hashed, name cleared).</li>
      </ul>

      <h3 style={s.h3}>Billing Records</h3>
      <p style={s.p}>
        Billing records (tier, subscription ID, invoice references) are retained as required by tax and accounting law (approximately 7 years), even after account deletion. These do not contain payment card information.
      </p>

      <h3 style={s.h3}>Your Right to Delete</h3>
      <p style={s.p}>You can request full data deletion at any time:</p>
      <ul style={s.ul}>
        <li style={s.li}>Via API: <code>DELETE /v1/account/data</code></li>
        <li style={s.li}>Via email: privacy@one-email.com</li>
        <li style={s.li}>Written confirmation provided on request.</li>
      </ul>

      {/* 8. Your Rights */}
      <h2 id="rights" style={s.h2}>8. Your Rights</h2>

      <h3 style={s.h3}>If You Are a One-Email Account Holder (We Are the Controller)</h3>
      <p style={s.p}>Under GDPR, UK GDPR, and similar laws, you have the right to:</p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Right</th>
              <th style={thStyle}>How to Exercise</th>
            </tr>
          </thead>
          <tbody>
            <tr><td style={tdStyle}><strong>Access</strong> your personal data</td><td style={tdStyle}>Email privacy@one-email.com or use the API</td></tr>
            <tr><td style={tdStyle}><strong>Rectify</strong> inaccurate data</td><td style={tdStyle}>Update your account in the dashboard</td></tr>
            <tr><td style={tdStyle}><strong>Erase</strong> your data</td><td style={tdStyle}><code>DELETE /v1/account/data</code> or email privacy@one-email.com</td></tr>
            <tr><td style={tdStyle}><strong>Export</strong> your data (portability)</td><td style={tdStyle}>Email privacy@one-email.com &mdash; we provide data in JSON format</td></tr>
            <tr><td style={tdStyle}><strong>Object</strong> to processing</td><td style={tdStyle}>Email privacy@one-email.com</td></tr>
            <tr><td style={tdStyle}><strong>Restrict</strong> processing</td><td style={tdStyle}>Email privacy@one-email.com &mdash; we will suspend processing while we review</td></tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>We respond to data subject requests within <strong>30 days</strong> (14 days for straightforward requests).</p>

      <h3 style={s.h3}>If You Are an Email Recipient (Our Customer Is the Controller)</h3>
      <p style={s.p}>
        If you received an email sent via One-Email and want to exercise your data rights (access, deletion, unsubscribe), please contact the <strong>sender</strong> &mdash; the company or person who sent you the email. They are the data controller and are responsible for responding to your request.
      </p>
      <p style={s.p}>
        If you contact us directly, we will direct you to the relevant customer. We do not independently act on data subject requests about recipient data without the customer&apos;s instruction.
      </p>
      <p style={s.p}>
        To <strong>unsubscribe</strong> from marketing emails sent via One-Email, use the one-click unsubscribe link in the email header or footer.
      </p>

      <h3 style={s.h3}>For California Residents (CCPA/CPRA)</h3>
      <p style={s.p}>One-Email acts as a <strong>service provider</strong> under the CCPA when processing data on behalf of customers who are covered businesses.</p>
      <ul style={s.ul}>
        <li style={s.li}>We do <strong>not</strong> sell personal information.</li>
        <li style={s.li}>We do <strong>not</strong> share personal information for cross-context behavioral advertising.</li>
        <li style={s.li}>California residents with questions may contact privacy@one-email.com.</li>
      </ul>

      <h3 style={s.h3}>For Brazilian Residents (LGPD)</h3>
      <p style={s.p}>
        One-Email complies with Brazil&apos;s Lei Geral de Prote&ccedil;&atilde;o de Dados. Brazilian residents may exercise their data rights by contacting privacy@one-email.com.
      </p>

      <h3 style={s.h3}>For Australian Residents</h3>
      <p style={s.p}>
        One-Email complies with the Australian Privacy Principles under the Privacy Act 1988. You may contact us at privacy@one-email.com to exercise your rights, or lodge a complaint with the Office of the Australian Information Commissioner (OAIC) at oaic.gov.au.
      </p>

      {/* 9. Cookies */}
      <h2 id="cookies" style={s.h2}>9. Cookies</h2>
      <p style={s.p}>
        We use <strong>only essential cookies</strong> for authentication. We do not use tracking cookies, advertising cookies, or third-party analytics cookies.
      </p>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Cookie</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Purpose</th>
              <th style={thStyle}>Attributes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Session cookie (Better Auth)</td>
              <td style={tdStyle}>Strictly necessary</td>
              <td style={tdStyle}>Maintains your login session on the dashboard</td>
              <td style={tdStyle}><code>httpOnly</code>, <code>Secure</code>, <code>SameSite=None</code>, 7-day expiry with 1-day refresh</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>
        Because we use only strictly necessary cookies, no cookie consent is legally required under the ePrivacy Directive. We still show a brief informational banner on first visit for transparency &mdash; it tells you we only use essential cookies, with no tracking or advertising. For full details, see our <a href="/legal/cookies">Cookie Policy</a>.
      </p>

      {/* 10. Open and Click Tracking */}
      <h2 id="tracking" style={s.h2}>10. Open and Click Tracking</h2>
      <p style={s.p}>If you enable open tracking or click tracking for your emails, One-Email will:</p>
      <ul style={s.ul}>
        <li style={s.li}>Insert a tracking pixel (1x1 transparent image) for open tracking</li>
        <li style={s.li}>Rewrite links to pass through our tracking endpoint for click tracking</li>
      </ul>
      <p style={s.p}>
        This tracking is performed on your behalf as the data controller. You are responsible for disclosing tracking to your email recipients in your own privacy policy.
      </p>

      {/* 11. DMARC Reports */}
      <h2 id="dmarc" style={s.h2}>11. DMARC Reports</h2>
      <p style={s.p}>
        If you configure DMARC for your sending domain through One-Email, aggregate DMARC reports may be sent to a One-Email address (e.g., dmarc@one-email.com). These reports contain information about email delivery from your domain and are used solely to help diagnose deliverability issues. We do not share DMARC reports with third parties.
      </p>

      {/* 12. International Data Transfers */}
      <h2 id="transfers" style={s.h2}>12. International Data Transfers</h2>
      <p style={s.p}>
        Our primary data storage is in the EU (Neon Postgres, Scaleway SMTP). For data that transits through non-EU infrastructure (primarily Cloudflare&apos;s global edge network), the following transfer mechanisms are in place:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Cloudflare:</strong> EU Standard Contractual Clauses (included in Cloudflare&apos;s DPA) + EU-US Data Privacy Framework certification</li>
        <li style={s.li}><strong>Polar:</strong> Per Polar&apos;s data protection practices</li>
        <li style={s.li}><strong>Vercel (auto-configure only):</strong> Used only when you explicitly authorize OAuth access</li>
      </ul>
      <p style={s.p}>
        Our <a href="/legal/dpa">Data Processing Agreement</a> includes Standard Contractual Clauses (EU Commission Module 2: Controller-to-Processor and Module 3: Processor-to-Processor) to cover any transfers outside the EU/EEA.
      </p>
      <p style={s.p}>
        For UK customers, our DPA includes the UK International Data Transfer Addendum per the ICO&apos;s approved format.
      </p>
      <p style={s.p}>
        Where EU, UK, or Swiss data protection law conflicts with other provisions of our terms, <strong>data protection law takes precedence</strong>.
      </p>

      {/* 13. Children's Privacy */}
      <h2 id="children" style={s.h2}>13. Children&apos;s Privacy</h2>
      <p style={s.p}>
        The Service is intended for use by businesses and developers who are at least 18 years of age. We do not knowingly collect personal information from children under 13 (or 16 in jurisdictions where that is the applicable age). If we learn that we have collected personal information from a child, we will delete it promptly.
      </p>

      {/* 14. Changes to This Policy */}
      <h2 id="changes" style={s.h2}>14. Changes to This Policy</h2>
      <p style={s.p}>
        We will notify you of material changes via email and a dashboard banner at least <strong>30 days</strong> before they take effect. Previous versions will be archived at <code>/legal/privacy/archive</code>. Continued use after the notice period constitutes acceptance.
      </p>

      {/* 15. Contact */}
      <h2 id="contact" style={s.h2}>15. Contact</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Privacy inquiries:</strong> privacy@one-email.com</li>
        <li style={s.li}><strong>Data protection officer:</strong> Not currently required at our scale (GDPR Article 37). We will appoint a DPO if and when required.</li>
        <li style={s.li}><strong>Supervisory authority:</strong> Integritetsskyddsmyndigheten (IMY) &mdash; <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer">imy.se</a></li>
      </ul>
    </div>
  )
}
